import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import {
    withResettable,
    withThresholdField,
} from 'modules/settings/components/custom-notifications/custom-notification-threshold.business';
import { useEffect, useMemo, useState } from 'react';
import { getCustomAlarmsFormValidation } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form/custom-alarms-form.validation';
import { mapComparableFormState } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form/custom-alarms-form.mapper';
import { useCustomAlarmsFormContext } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form-context/use-custom-alarms-form-context.hook';
import {
    CustomAlarmFormState,
    NotificationFormState,
    NotificationLevel,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/settings-devices-custom-alarm-form.model';
import { useRegisteredEventsFetcher } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-registered-events-fetcher/use-registered-events-fetcher.hook';
import { mapCustomAlarmFromDomainToForm } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-data-handler/use-custom-alarms-data-handler.mapper';
import { CUSTOM_ALARMS_FORM_DEFAULT_FORM } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/settings-devices-custom-alarm-form.constants';

interface UseCustomAlarmsDataHandlerProps {
    deviceTypeId?: string;
    onClose: VoidFunction;
}

export const useCustomAlarmsDataHandler = ({ deviceTypeId, onClose }: UseCustomAlarmsDataHandlerProps) => {
    const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
    const [notificationsTouched, setNotificationsTouched] = useState<boolean>(false);
    const [form, setForm] = useState(structuredClone(CUSTOM_ALARMS_FORM_DEFAULT_FORM));

    // Baseline used to detect real user changes. It starts as the default form (add mode)
    // and is replaced by the prefilled definition once it is loaded from the API (edit mode),
    // so prefilling the form does not count as an unsaved change.
    const [baselineComparableState, setBaselineComparableState] = useState(() =>
        mapComparableFormState(CUSTOM_ALARMS_FORM_DEFAULT_FORM)
    );

    const { nameValid, eventsValid, windowDaysValidity, formValid, getNameInputError } = getCustomAlarmsFormValidation(
        form,
        submitAttempted
    );
    const { customAlarmDefinition } = useCustomAlarmsFormContext();
    const { registeredEvents, isLoadingEvents } = useRegisteredEventsFetcher({ deviceTypeId });

    useEffect(() => {
        if (!customAlarmDefinition) {
            return;
        }

        const prefilledForm = mapCustomAlarmFromDomainToForm(customAlarmDefinition);
        setBaselineComparableState(mapComparableFormState(prefilledForm));
        setForm(prefilledForm);
    }, [customAlarmDefinition]);

    const selectedEvents = useMemo(
        () => registeredEvents.filter((event) => form.selectedEventIds.includes(event.id)),
        [registeredEvents, form.selectedEventIds]
    );

    const eventsOptions = useMemo(
        () =>
            registeredEvents.map((event) => ({
                id: event.id,
                name: event.displayName,
            })),
        [registeredEvents]
    );

    const currentComparableState = useMemo(() => mapComparableFormState(form), [form]);
    const hasFormChanges = useMemo(
        () => JSON.stringify(currentComparableState) !== JSON.stringify(baselineComparableState),
        [currentComparableState, baselineComparableState]
    );

    // Submitting is only allowed for a valid form with actual changes, so prefilled
    // edit forms start with the save button disabled until the user edits something.
    const canSubmit = formValid && hasFormChanges;

    const unsavedData = useCheckUnsavedChanges(onClose);

    useEffect(() => {
        unsavedData.setIsUnsavedChanges(hasFormChanges);
    }, [hasFormChanges, unsavedData]);

    const setField = <K extends keyof CustomAlarmFormState>(field: K, value: CustomAlarmFormState[K]) => {
        setForm((previousState) => ({ ...previousState, [field]: value }));
    };

    const setNotificationField = <K extends keyof NotificationFormState>(
        notification: NotificationLevel,
        field: K,
        value: NotificationFormState[K]
    ) => {
        setNotificationsTouched(true);
        setForm((previousState) => withThresholdField(previousState, notification, field, value));
    };

    const applyEventSelection = (selectedEventIds: string[]) => {
        setForm((previousState) => ({
            ...previousState,
            selectedEventIds,
            eventDraftIds: selectedEventIds,
        }));
    };

    const setResettable = (isResettable: boolean) => {
        setForm((previousState) => withResettable(previousState, isResettable));
    };

    const markSubmitAttempted = () => {
        setSubmitAttempted(true);
    };

    return {
        applyEventSelection,
        eventsOptions,
        eventsValid,
        form,
        formValid,
        windowDaysValidity,
        getNameInputError,
        canSubmit,
        isLoadingEvents,
        markSubmitAttempted,
        nameValid,
        notificationsTouched,
        selectedEvents,
        setField,
        setNotificationField,
        setResettable,
        submitAttempted,
        unsavedData,
    };
};
